import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import EntryStatus from '../../atoms/entry-status';
import ENTRY_STATUS from '../../../constants/entry-status';
import PreSettingDone from '../../templates/pre-setting-done';
import Http from "../../../http";
import Transition from '../../../utils/transition';
import { FinalPriceInfo, PaymentInfo, ShippingAddressInfo, Message, Spinner, FormatUtil } from "@graffer-inc/graffer-ui";
import EntryDetailInfo from "../../organisms/entry/entry-detail-info";
import IssueInfoRegisterForm from "../../organisms/entry/issue-info-register-form";
import { toast } from "react-toastify";
import ErrorHandler from "../../../utils/error-handler";
import File from "../../../utils/file";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import APPLICATION_TYPE from "../../../constants/application-type";
import Price from "../../../utils/price";
import Step from "../../organisms/entry/step";
import CertificateImage from '../../../assets/images/certificate.png';

const Main = ({ match }) => {
  const { entryId } = match.params;
  const [entryInfo, setEntryInfo] = useState(null);
  const [registryOffice, setRegistryOffice] = useState(null);
  const [isMapOff, setIsMapOff] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchEntryInfo = async () => {
    await Http.fetchEntry(
      entryId,
      res => setEntryInfo(res.data),
      () => Transition.goTo("/")
    );
  };

  const fetchRegistryOffice = async () => {
    await Http.fetchRegistryOffice(
      entryId,
      res => {
        setRegistryOffice(res.data.registryOffice);
      },
      () => setIsMapOff(true),
    );
  };

  useEffect(() => {
    fetchEntryInfo();
    fetchRegistryOffice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const registerIssueInfo = async ({ serialNo, elCertPassword, isRegistryCertsRegistration }) => {
    setIsLoading(true);
    const body = { serialNo, elCertPassword, isRegistryCertsRegistration, versionNo: entryInfo.entry.versionNo };
    await Http.registerIssueInfo(
      entryId,
      body,
      async () => {
        toast.success("登録が完了しました。");
        await Http.fetchEntry(
          entryId,
          res => setEntryInfo(res.data),
          () => Transition.goTo("/")
        );
        window.scrollTo(0, 0);
      },
      err => {
        setIsLoading(false);
        ErrorHandler.common(err.response.status);
      }
    );
  };

  const downloadElCert = async () => {
    setIsDownloading(true);
    await Http.fetchElCert(
      entryId,
      async (res) => {
        try {
          File.download(window.atob(res.data.encoded), `電子証明書_${entryId}.p12`, 'application/octet-stream', true);
          toast.success('ダウンロードが完了しました。');
          Transition.goTo(`/entries/${entryId}/complete`)
        } catch (e) {
          console.error(e);
        }
      },
      (err) => {
        setIsDownloading(false);
        if ([401, 403].includes(err.response.status)) {
          Transition.goToEmsLogin();
        } else {
          toast.error('電子証明書のダウンロードに失敗しました。画面をリロードまたは再ログインしてください。');
        }
      }
    );
  };

  const priceInfos = !entryInfo ? [] : [
    {
      name: `${APPLICATION_TYPE.name(entryInfo.entry.applicationType)}プラン料金(税抜)`,
      count: 1,
      price: APPLICATION_TYPE.CODE.WINDOW === entryInfo.entry.applicationType ? entryInfo.priceTable.price : entryInfo.priceTable.deliveryPrice,
    },
  ];
  if (entryInfo && entryInfo.entry.isWithRevenueStamp) {
    priceInfos.push({
      name: '収入印紙セット料金(税抜)',
      count: 1,
      price: Price.getRevenueStampPrice(entryInfo.entry.certPeriod, entryInfo.priceTable.revenueStampFeeRate, entryInfo.entry.createdAt),
    });
  }

  return (
    <PreSettingDone>
      <div className="container">
        <p className="is-size-3 has-text-weight-bold">申込内容の詳細</p>
        {
          (entryInfo && entryInfo.entry.status !== ENTRY_STATUS.CODE.DOWNLOADED) && (
            <div style={{ padding: '10px 0 15px 0' }}>
              <Step status={entryInfo.entry.status} />
            </div>
          )
        }
        <div style={{ padding: '10px 0 20px 0' }}>
          <div style={{ margin: '0 0 5px 0' }}>
            <EntryStatus status={entryInfo && entryInfo.entry.status} isRefunded={entryInfo && entryInfo.entry.isRefunded} />
          </div>
          {
            (entryInfo && !entryInfo.entry.isRefunded) && (
              <>
                {/* 発送準備中の場合 */}
                {
                  entryInfo && entryInfo.entry.status === ENTRY_STATUS.CODE.ORDERED && (
                    <Message color="is-info" fontSize="is-size-6">
                      <p className="is-size-7-mobile has-text-weight-bold">取得サポートキットの発送が完了次第、メールにてご連絡させていただきます。<span className="is-highlight">取得サポートキットの到着をお待ちください。</span></p>
                      <p className="is-size-7-mobile has-text-weight-bold">申請のためにご用意いただくものは<span className="is-highlight">{entryInfo.entry.attorneyName ? '「登記所に提出した印鑑」「代理人の認印」の2点' : '「登記所に提出した印鑑」'}</span>です。</p>
                      <p className="is-size-7-mobile">取得サポートキットが手元に届いたら、電子証明書取得マニュアルを参考に手続きを進めてください。</p>
                      {registryOffice && (
                        <p className="is-size-7-mobile">
                          <span className="has-text-weight-bold">申請先の法務局は<span className="is-highlight">「{registryOffice.name}{registryOffice.branch}」</span>です。</span>
                          {entryInfo.entry.applicationType === APPLICATION_TYPE.CODE.WINDOW && (
                            <span>場所は下記のマップからご確認いただけます。</span>
                          )}
                        </p>
                      )}
                    </Message>
                  )
                }
                {/* 発送完了の場合 */}
                {
                  entryInfo && entryInfo.entry.status === ENTRY_STATUS.CODE.SHIPPED && (
                    <Message color="is-info">
                      <p>取得サポートキットの配送状況は郵便局の集荷手続きが完了後に、<a href={`https://trackings.post.japanpost.jp/services/sp/srv/search/?search=開始&locale=ja&requestNo1=${entryInfo.entry.trackingNo}`} target="_blank" rel="noopener noreferrer">こちら</a>からご確認いただけます。お手元に届き次第、電子証明書取得マニュアルを参考に手続きを進めてください。</p>
                      <p className="has-text-weight-bold">申請のためにご用意いただくものは<span className="is-highlight">{entryInfo.entry.attorneyName ? '「登記所に提出した印鑑」「代理人の認印」の2点' : '「登記所に提出した印鑑」'}</span>です。</p>
                      {registryOffice && (
                        <p>
                          <span className="has-text-weight-bold">申請先の法務局は<span className="is-highlight">「{registryOffice.name}{registryOffice.branch}」</span>です。</span>
                          {entryInfo.entry.applicationType === APPLICATION_TYPE.CODE.WINDOW && (
                            <span>場所は下記のマップからご確認いただけます。</span>
                          )}
                        </p>
                      )}
                      <p className="has-text-weight-bold">申請が完了したら、電子証明書発行確認票に<span className="is-highlight">記載されたシリアル番号と設定したい電子証明書のパスワードを登録してください。</span></p>
                    </Message>
                  )
                }
                {/* ダウンロード準備中の場合 */}
                {
                  entryInfo && entryInfo.entry.status === ENTRY_STATUS.CODE.REGISTERED && (
                    <Message color="is-info">
                      <p>ご登録いただいた情報を元に電子証明書を発行し、ダウンロードいただけるように準備をします。</p>
                      <p><span className="has-text-weight-bold">準備が整い次第、メールにてご連絡させていただきます。<span className="is-highlight">準備の完了までお待ちください。</span></span></p>
                      {/* eslint-disable-next-line */}
                      <p>翌営業日までに連絡がなかった場合、大変お手数ですが<a onClick={() => Transition.newTab('https://form.run/@graffer-el-cert')}>こちら</a>からお問い合わせをお願いします。</p>
                    </Message>
                  )
                }
                {/* 未ダウンロードの場合 */}
                {
                  entryInfo && entryInfo.entry.status === ENTRY_STATUS.CODE.DOWNLOAD_AVAILABLE && (
                    <Message color="is-warning">
                      <p className="has-text-weight-bold"><span className="is-highlight">セキュリティの観点から電子証明書のダウンロードは一度のみとなります。</span>ネットワークの良い環境で電子証明書を保管するパソコンからダウンロードしてください。</p>
                    </Message>
                  )
                }
                {/* ダウンロード済の場合 */}
                {
                  entryInfo && entryInfo.entry.status === ENTRY_STATUS.CODE.DOWNLOADED && (
                    <Message color="is-info">
                      <p>電子証明書の証明期間は登記所への申請が完了した日から開始となります。</p>
                      {/* eslint-disable-next-line */}
                      <p>電子証明書の再ダウンロードをご希望の場合、<a onClick={() => Transition.newTab('https://form.run/@graffer-el-cert')}>こちら</a>からお問い合わせをお願いします。</p>
                    </Message>
                  )
                }
              </>
            )
          }
        </div>
        {
          (entryInfo && !entryInfo.entry.isRefunded) && (
            <>
              {/* 発送準備中の場合 */}
              {
                entryInfo && entryInfo.entry.status === ENTRY_STATUS.CODE.ORDERED && (
                  <>
                    <div className={`columns ${!isMapOff ? 'is-centered' : ''}`}>
                      {
                        // 管轄登記所の取得呼び出しに失敗した場合、Mapは非表示にする
                        !isMapOff && entryInfo.entry.applicationType === APPLICATION_TYPE.CODE.WINDOW && (
                          <div className="column">
                            {
                              registryOffice ? (
                                <div style={{ height: '400px' }}>
                                  <iframe
                                    title="管轄法務局"
                                    style={{ border: 'none', width: '100%', height: '100%' }}
                                    src={`https://maps.google.co.jp/maps?output=embed&q=${registryOffice.name}${registryOffice.branch}`}
                                  />
                                </div>
                              ) : (
                                <div className="has-text-centered" style={{ padding: '100px 0' }}>
                                  <Spinner />
                                </div>
                              )
                            }
                          </div>
                        )
                      }
                    </div>
                    <br />
                    <div className="is-divider" data-content="以下申し込み内容の詳細" />
                    <br />
                  </>
                )
              }
              {/* 発送完了の場合 */}
              {
                entryInfo && entryInfo.entry.status === ENTRY_STATUS.CODE.SHIPPED && (
                  <>
                    <div className={`columns ${!isMapOff ? 'is-centered' : ''}`}>
                      {
                        // 管轄登記所の取得呼び出しに失敗した場合、Mapは非表示にする
                        !isMapOff && entryInfo.entry.applicationType === APPLICATION_TYPE.CODE.WINDOW && (
                          <div className="column is-6">
                            {
                              registryOffice ? (
                                <div style={{ height: '100%' }}>
                                  <iframe
                                    title="管轄法務局"
                                    style={{ border: 'none', width: '100%', height: '100%' }}
                                    src={`https://maps.google.co.jp/maps?output=embed&q=${registryOffice.name}${registryOffice.branch}`}
                                  />
                                </div>
                              ) : (
                                <div className="has-text-centered" style={{ padding: '100px 0' }}>
                                  <Spinner />
                                </div>
                              )
                            }
                          </div>
                        )
                      }
                      <div className="column is-6">
                        <div className="flat-box">
                          <IssueInfoRegisterForm
                            onExecute={registerIssueInfo}
                            isLoading={isLoading}
                          />
                        </div>
                      </div>
                    </div>
                    <br />
                    <div className="is-divider" data-content="以下申し込み内容の詳細" />
                    <br />
                  </>
                )
              }
              {/* 未ダウンロードの場合 */}
              {
                entryInfo && entryInfo.entry.status === ENTRY_STATUS.CODE.DOWNLOAD_AVAILABLE && (
                  <>
                    <div className="columns is-centered">
                      <div className="column is-5">
                        <div className="has-text-centered">
                          <img src={CertificateImage} alt="電子証明書イメージ" width="250px" />
                        </div>
                        <button className="is-hidden-tablet button is-accent is-rounded is-fullwidth" disabled>パソコンからダウンロードしてください</button>
                        <button
                          className="is-hidden-mobile button is-accent is-rounded is-fullwidth"
                          disabled={isDownloading}
                          onClick={() => downloadElCert()}
                        >
                          <span className="icon">
                            <FontAwesomeIcon icon={faDownload} />
                          </span>
                          <span>
                            {isDownloading ? 'ダウンロードしています...' : '電子証明書をダウンロードする'}
                          </span>
                        </button>
                      </div>
                    </div>
                    <br />
                    <div className="is-divider" data-content="以下申し込み内容の詳細" />
                    <br />
                  </>
                )
              }
            </>)
        }
        <div className="columns">
          <div className="column is-8">
            <div className="columns">
              <div className="column is-8">
                <ShippingAddressInfo
                  canChange
                  isLoading={!entryInfo}
                  shippingAddress={entryInfo && entryInfo.shippingAddress}
                  className="flat-box"
                />
              </div>
              <div className="column is-4">
                <PaymentInfo
                  canChange
                  isLoading={!entryInfo}
                  brand={entryInfo ? entryInfo.charge.brand : ''}
                  last4={entryInfo ? entryInfo.charge.last4 : ''}
                  className="flat-box"
                />
              </div>
            </div>
            <div className="columns">
              <div className="column">
                <EntryDetailInfo isLoading={!entryInfo} entry={entryInfo && Object.assign({ isAttorneyApplication: entryInfo.entry.attorneyName && entryInfo.entry.attorneyAddress }, entryInfo.entry)} />
              </div>
            </div>
          </div>
          <div className="column is-4">
            <div>
              <div className="has-text-right">
                <p className="is-size-7 has-text-grey">
                  申込番号: {entryInfo ? entryInfo.entry.id : '読み込み中...'}
                </p>
                <p className="is-size-7 has-text-grey">
                  申込日時: {entryInfo ? FormatUtil.dateWithTime(entryInfo.entry.createdAt) : '読み込み中...'}
                </p>
              </div>
              <FinalPriceInfo
                tax={entryInfo && entryInfo.priceTable.tax}
                priceInfos={priceInfos}
                isLoading={!entryInfo}
              />
            </div>
          </div>
        </div>
        <div>
          <p className="is-size-7">
            <Link to={`/entries/${entryId}/invoice`}>領収書をダウンロードする</Link>
          </p>
        </div>

      </div>
    </PreSettingDone>
  )
};

export default Main;